import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  FaCrown, FaServer, FaShieldAlt, FaSatellite, FaDatabase, 
  FaExclamationTriangle, FaChartLine, FaSlidersH, FaFileExport, 
  FaLock, FaCheckCircle, FaTimesCircle, FaCloudSun, FaMicroscope
} from 'react-icons/fa';
import './SuperAdmin.css';

const SuperAdmin = () => {
  const navigate = useNavigate();
  const [isSuperAuth, setIsSuperAuth] = useState(() => {
    return localStorage.getItem('is_fd_super_admin') === 'true';
  });
  const [masterPasscode, setMasterPasscode] = useState('');
  const [authError, setAuthError] = useState('');

  const [activeTab, setActiveTab] = useState('telemetry'); // telemetry | heatmap | guardrails | claims | database
  
  // Guardrail Configuration State
  const [guardrails, setGuardrails] = useState({
    maxSprayTempCelsius: 32,
    maxWindSpeedKmh: 15,
    cibrcRegistryStrictLock: true,
    blockSystemicInFlowering: true,
    mandatoryRainfastnessHours: 4,
    organicFirstThreshold: 45 // When DRI < 45, prioritize bio-pesticides
  });

  const [saveGuardrailMsg, setSaveGuardrailMsg] = useState(false);

  // System Telemetry Live Status
  const apisList = [
    { name: 'MongoDB Atlas (Cluster: drishti)', type: 'Database v8.0.29', latency: '42 ms', uptime: '99.99%', status: 'Operational', icon: <FaDatabase /> },
    { name: 'Sentinel-2 & Landsat-8 NDVI', type: 'Satellite Remote Sensing', latency: '180 ms', uptime: '99.8%', status: 'Operational', icon: <FaSatellite /> },
    { name: 'IMD Doppler Weather & Rainfall', type: 'National Climate Feed', latency: '95 ms', uptime: '99.9%', status: 'Operational', icon: <FaCloudSun /> },
    { name: 'Data.gov.in / Agmarknet APMC', type: 'Mandi Price Scraper & API', latency: '210 ms', uptime: '99.5%', status: 'Operational', icon: <FaChartLine /> },
    { name: 'Google Gemini 2.5 Vision & Chat', type: 'AI Explainer & Guardrail', latency: '145 ms', uptime: '100%', status: 'Operational', icon: <FaMicroscope /> }
  ];

  // Statewide District Vulnerability Data
  const districtsHeatmap = [
    { district: 'Jalgaon', state: 'Maharashtra', primaryCrop: 'Banana (GI-Tag G9)', farmers: 1420, driAvg: 72.4, status: 'Severe Drought & Heatwave', alertLevel: 'RED' },
    { district: 'Nashik', state: 'Maharashtra', primaryCrop: 'Onion & Grapes', farmers: 1180, driAvg: 48.6, status: 'Market Volatility & Price Deficit', alertLevel: 'AMBER' },
    { district: 'Indore', state: 'Madhya Pradesh', primaryCrop: 'Soybean & Wheat', farmers: 950, driAvg: 51.2, status: 'Soil Moisture Stress', alertLevel: 'AMBER' },
    { district: 'Nagpur', state: 'Maharashtra', primaryCrop: 'Orange & Cotton', farmers: 870, driAvg: 32.1, status: 'Normal Growth Cycle', alertLevel: 'GREEN' },
    { district: 'Pune', state: 'Maharashtra', primaryCrop: 'Sugarcane & Vegetables', farmers: 640, driAvg: 28.5, status: 'Optimal Water Inflow', alertLevel: 'GREEN' },
    { district: 'Dhule', state: 'Maharashtra', primaryCrop: 'Cotton & Maize', farmers: 520, driAvg: 64.8, status: 'Rainfall Deficit (-38%)', alertLevel: 'RED' }
  ];

  const handleSuperLogin = (e) => {
    e.preventDefault();
    if (masterPasscode === '9822725265' || masterPasscode.toLowerCase() === 'superadmin' || masterPasscode.toLowerCase() === 'admin') {
      setIsSuperAuth(true);
      localStorage.setItem('is_fd_super_admin', 'true');
      setAuthError('');
    } else {
      setAuthError('Unauthorized Master Passcode. Access denied.');
    }
  };

  const handleSuperLogout = () => {
    setIsSuperAuth(false);
    localStorage.removeItem('is_fd_super_admin');
    navigate('/login');
  };

  const handleGuardrailSave = (e) => {
    e.preventDefault();
    setSaveGuardrailMsg(true);
    setTimeout(() => setSaveGuardrailMsg(false), 2500);
  };

  if (!isSuperAuth) {
    return (
      <div className="fd-super-login-wrap">
        <div className="fd-super-login-card fd-card">
          <div className="fd-super-crown-icon">
            <FaCrown />
          </div>
          <h2>Fasal Drishti Super Admin</h2>
          <p>State Agricultural Command Center &amp; AI Policy Master Controller.</p>

          <form onSubmit={handleSuperLogin} className="fd-super-login-form">
            <div className="fd-super-input-box">
              <FaLock className="fd-super-lock-icn" />
              <input 
                type="password" 
                placeholder="Enter Master Security Key (e.g. 9822725265)"
                value={masterPasscode}
                onChange={(e) => setMasterPasscode(e.target.value)}
                required
                autoFocus
              />
            </div>
            {authError && <div className="fd-super-error">{authError}</div>}
            <button type="submit" className="fd-super-submit-btn">
              Authenticate Master Console →
            </button>
          </form>
          <Link to="/dashboard" className="fd-super-back-link">
            ← Return to Standard App
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="fd-super-page">
      <div className="fd-super-container">
        {/* Top Master Header */}
        <div className="fd-super-header">
          <div className="fd-super-title-row">
            <div className="fd-super-badge-icon">
              <FaCrown />
            </div>
            <div>
              <div className="fd-super-tag">SUPER ADMIN MASTER CONSOLE</div>
              <h1>National Agritech Intelligence &amp; AI Safety Command Center</h1>
            </div>
          </div>

          <div className="fd-super-header-right">
            <span className="fd-super-health-chip">
              <span className="fd-pulse-dot"></span> All Systems Normal
            </span>
            <button className="fd-super-logout-btn" onClick={handleSuperLogout}>
              Exit Super Admin
            </button>
          </div>
        </div>

        {/* Global Summary KPI Banners */}
        <div className="fd-super-kpi-grid">
          <div className="fd-super-kpi-card fd-card">
            <span className="fd-kpi-lbl">Total Monitored Acres</span>
            <span className="fd-kpi-val">128,450 Acres</span>
            <span className="fd-kpi-sub">Across 18 Agricultural Clusters</span>
          </div>

          <div className="fd-super-kpi-card fd-card">
            <span className="fd-kpi-lbl">Avg Statewide Distress (DRI)</span>
            <span className="fd-kpi-val text-red">54.2 / 100</span>
            <span className="fd-kpi-sub">Khandesh Banana Belt in Red Zone</span>
          </div>

          <div className="fd-super-kpi-card fd-card">
            <span className="fd-kpi-lbl">PMFBY Early Payout Saved</span>
            <span className="fd-kpi-val text-green">₹ 4.82 Crores</span>
            <span className="fd-kpi-sub">Pre-loss intervention prevented crop failure</span>
          </div>

          <div className="fd-super-kpi-card fd-card">
            <span className="fd-kpi-lbl">Zero-Hallucination Safe Sprays</span>
            <span className="fd-kpi-val text-blue">100% CIBRC Locked</span>
            <span className="fd-kpi-sub">0 Chemical damage incidents reported</span>
          </div>
        </div>

        {/* Super Admin Navigation Tabs */}
        <div className="fd-super-tabs">
          <button 
            className={`fd-super-tab-btn ${activeTab === 'telemetry' ? 'active' : ''}`}
            onClick={() => setActiveTab('telemetry')}
          >
            🛰️ API &amp; Satellite Telemetry
          </button>
          <button 
            className={`fd-super-tab-btn ${activeTab === 'heatmap' ? 'active' : ''}`}
            onClick={() => setActiveTab('heatmap')}
          >
            🗺️ GIS District Distress Heatmap
          </button>
          <button 
            className={`fd-super-tab-btn ${activeTab === 'guardrails' ? 'active' : ''}`}
            onClick={() => setActiveTab('guardrails')}
          >
            🛡️ AI Chemical Safety Guardrails
          </button>
          <button 
            className={`fd-super-tab-btn ${activeTab === 'database' ? 'active' : ''}`}
            onClick={() => setActiveTab('database')}
          >
            🗄️ MongoDB Atlas Master Explorer
          </button>
        </div>

        {/* Tab 1: Live API Telemetry & Health */}
        {activeTab === 'telemetry' && (
          <div className="fd-super-section-card fd-card">
            <div className="fd-super-sec-head">
              <h3>🛰️ Real-Time Telemetry, Remote Sensing &amp; AI Service Mesh</h3>
              <button className="fd-super-btn-sec" onClick={() => alert('All API Pipelines Verified!')}>
                ⚡ Test All Endpoints
              </button>
            </div>

            <div className="fd-telemetry-grid">
              {apisList.map((api, idx) => (
                <div key={idx} className="fd-telemetry-row fd-card">
                  <div className="fd-tel-left">
                    <div className="fd-tel-icon">{api.icon}</div>
                    <div>
                      <h4>{api.name}</h4>
                      <span className="fd-tel-type">{api.type}</span>
                    </div>
                  </div>
                  <div className="fd-tel-right">
                    <span className="fd-tel-latency">⚡ {api.latency}</span>
                    <span className="fd-tel-uptime">Uptime: {api.uptime}</span>
                    <span className="fd-badge-low">
                      <FaCheckCircle /> {api.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab 2: GIS District Heatmap */}
        {activeTab === 'heatmap' && (
          <div className="fd-super-section-card fd-card">
            <div className="fd-super-sec-head">
              <h3>🗺️ Regional District Distress Vulnerability Matrix</h3>
              <span className="fd-heat-badge">Live IMD &amp; APMC Calibration</span>
            </div>

            <div className="fd-super-table-wrap">
              <table className="fd-super-table">
                <thead>
                  <tr>
                    <th>District</th>
                    <th>State</th>
                    <th>Dominant Crop</th>
                    <th>Active Farmers</th>
                    <th>Average DRI</th>
                    <th>Vulnerability Level</th>
                    <th>Primary Stress Trigger</th>
                  </tr>
                </thead>
                <tbody>
                  {districtsHeatmap.map((d, i) => (
                    <tr key={i}>
                      <td className="fd-td-bold">📍 {d.district}</td>
                      <td>{d.state}</td>
                      <td>🌱 {d.primaryCrop}</td>
                      <td>{d.farmers.toLocaleString()}</td>
                      <td>
                        <strong style={{ color: d.alertLevel === 'RED' ? '#dc2626' : (d.alertLevel === 'AMBER' ? '#d97706' : '#059669') }}>
                          {d.driAvg} / 100
                        </strong>
                      </td>
                      <td>
                        <span className={`fd-badge-${d.alertLevel === 'RED' ? 'high' : (d.alertLevel === 'AMBER' ? 'mod' : 'low')}`}>
                          {d.alertLevel === 'RED' ? 'CRITICAL (RED)' : (d.alertLevel === 'AMBER' ? 'ELEVATED (AMBER)' : 'NORMAL (GREEN)')}
                        </span>
                      </td>
                      <td className="fd-td-sub">{d.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab 3: Chemical Safety Guardrails */}
        {activeTab === 'guardrails' && (
          <div className="fd-super-section-card fd-card">
            <div className="fd-super-sec-head">
              <h3>🛡️ Zero-Hallucination Chemical &amp; Agronomic Guardrail Configuration</h3>
            </div>

            <form onSubmit={handleGuardrailSave} className="fd-guardrails-form">
              <div className="fd-gr-grid">
                <div className="fd-gr-field">
                  <label>Max Safe Spray Temperature (°C):</label>
                  <input 
                    type="number" 
                    value={guardrails.maxSprayTempCelsius} 
                    onChange={(e) => setGuardrails({ ...guardrails, maxSprayTempCelsius: Number(e.target.value) })}
                  />
                  <small>Sulfur &amp; Copper sprays strictly blocked when temperature exceeds this limit.</small>
                </div>

                <div className="fd-gr-field">
                  <label>Max Safe Wind Velocity (km/h):</label>
                  <input 
                    type="number" 
                    value={guardrails.maxWindSpeedKmh} 
                    onChange={(e) => setGuardrails({ ...guardrails, maxWindSpeedKmh: Number(e.target.value) })}
                  />
                  <small>Chemical drift mitigation lock.</small>
                </div>

                <div className="fd-gr-field">
                  <label>Mandatory Rainfastness Lookahead (Hours):</label>
                  <input 
                    type="number" 
                    value={guardrails.mandatoryRainfastnessHours} 
                    onChange={(e) => setGuardrails({ ...guardrails, mandatoryRainfastnessHours: Number(e.target.value) })}
                  />
                  <small>Spraying delayed if rain forecasted within this timeframe.</small>
                </div>

                <div className="fd-gr-field">
                  <label>Organic / Bio-Pesticide Priority Threshold (DRI):</label>
                  <input 
                    type="number" 
                    value={guardrails.organicFirstThreshold} 
                    onChange={(e) => setGuardrails({ ...guardrails, organicFirstThreshold: Number(e.target.value) })}
                  />
                  <small>Below this DRI score, prioritize Neem Oil &amp; Trichoderma over synthetics.</small>
                </div>
              </div>

              <div className="fd-gr-toggles">
                <label className="fd-toggle-row">
                  <input 
                    type="checkbox" 
                    checked={guardrails.cibrcRegistryStrictLock}
                    onChange={(e) => setGuardrails({ ...guardrails, cibrcRegistryStrictLock: e.target.checked })}
                  />
                  <span>Enforce Strict CIBRC / ICAR Insecticides Act 1968 Formulation Registry</span>
                </label>

                <label className="fd-toggle-row">
                  <input 
                    type="checkbox" 
                    checked={guardrails.blockSystemicInFlowering}
                    onChange={(e) => setGuardrails({ ...guardrails, blockSystemicInFlowering: e.target.checked })}
                  />
                  <span>Hard-Block Systemic Neonicotinoids during Flowering (Pollinator Protection)</span>
                </label>
              </div>

              <div className="fd-gr-action-row">
                <button type="submit" className="fd-super-submit-btn" style={{ width: 'fit-content' }}>
                  <FaSlidersH /> Update AI Safety Policy Rules
                </button>
                {saveGuardrailMsg && (
                  <span className="fd-gr-success-badge">
                    ✓ AI Safety Guardrail Rules successfully updated and deployed to live models!
                  </span>
                )}
              </div>
            </form>
          </div>
        )}

        {/* Tab 4: MongoDB Master Data Explorer */}
        {activeTab === 'database' && (
          <div className="fd-super-section-card fd-card">
            <div className="fd-super-sec-head">
              <h3>🗄️ MongoDB Atlas Production Collections (`fasal_drishti`)</h3>
              <button className="fd-super-btn-sec" onClick={() => alert('Exporting MongoDB collection schema backup...')}>
                <FaFileExport /> Export JSON Backup
              </button>
            </div>

            <div className="fd-mongo-collections-grid">
              <div className="fd-collection-box fd-card">
                <h4>🧑‍🌾 `farmers`</h4>
                <p>Stores Farmer credentials, phone, Google UIDs, GPS farm locations, land sizes &amp; primary crop.</p>
                <div className="fd-coll-stat"><strong>Indexes:</strong> `email_1`, `phone_1`, `google_uid_1`</div>
              </div>

              <div className="fd-collection-box fd-card">
                <h4>📊 `risk_assessments`</h4>
                <p>Stores 0-100 DRI logs, 5-factor weights (Weather, Soil, Water, Market, Spoilage) and explainability notes.</p>
                <div className="fd-coll-stat"><strong>Indexes:</strong> `farmer_id_1`, `timestamp_-1`</div>
              </div>

              <div className="fd-collection-box fd-card">
                <h4>🛒 `mandi_prices`</h4>
                <p>Cached Agmarknet &amp; Data.gov.in daily wholesale arrivals and modal price trends.</p>
                <div className="fd-coll-stat"><strong>Indexes:</strong> `state_1_district_1_commodity_1`</div>
              </div>

              <div className="fd-collection-box fd-card">
                <h4>📋 `interventions` &amp; `farm_activities`</h4>
                <p>Farmer 24-48h action checklists, PMFBY claim records, spraying, and fertilizer logs.</p>
                <div className="fd-coll-stat"><strong>Indexes:</strong> `farmer_id_1`, `created_at_-1`</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdmin;
